<template>
  <div class="login-page min-h-screen flex pt-20">
    <div class="container mx-auto px-4 mt-8">
      <div class="flex justify-center">
        <div class="w-full max-w-md">
          <UiCard class="shadow-sm">
            <UiCardContent class="p-6">
              <div class="mb-6">
                <h2 class="text-xl font-semibold mb-1">Login</h2>
                <p class="text-sm text-muted-foreground">Enter your email below to login to your account</p>
              </div>
              
              <div v-if="sessionExpired" class="mb-4 p-3 bg-amber-50 text-amber-700 border border-amber-200 rounded-md flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-2">
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                  <path d="M12 9v4"/>
                  <path d="M12 17h.01"/>
                </svg>
                Your session has expired. Please log in again to continue.
              </div>
              
              <div v-if="registrationSuccess" class="mb-4 p-3 bg-green-50 text-green-700 border border-green-200 rounded-md flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Registration successful! Please log in with your new account.
              </div>
              
              <div v-if="error" class="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded-md">
                {{ error }}
              </div>
              
              <form @submit.prevent="handleLogin" class="space-y-4">
                <div class="space-y-2">
                  <UiLabel htmlFor="email" class="text-sm">Email</UiLabel>
                  <UiInput 
                    type="email" 
                    id="email" 
                    v-model="credentials.Email" 
                    required
                    autocomplete="email"
                    placeholder="me@example.com"
                  />
                </div>
                
                <div class="space-y-2">
                  <div class="flex justify-between items-center">
                    <UiLabel htmlFor="password" class="text-sm">Password</UiLabel>
                    <a href="#" tabindex="-1" class="text-xs text-muted-foreground hover:underline">Forgot password eh?</a>
                  </div>
                  <UiInput 
                    type="password" 
                    id="password" 
                    v-model="credentials.Password" 
                    required
                    autocomplete="current-password"
                  />
                </div>
                
                <UiButton 
                  type="submit" 
                  class="w-full bg-black hover:bg-black/90" 
                  :disabled="loading"
                >
                  <svg v-if="loading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sign in
                </UiButton>
                
                <div class="text-center mt-4">
                  <p class="text-sm text-muted-foreground">
                    Don't have an account? 
                    <router-link to="/register" class="text-primary hover:underline">Register</router-link>
                  </p>
                </div>
              </form>
            </UiCardContent>
          </UiCard>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { useRoute } from 'vue-router';
import UiCard from '@/components/ui/card.vue';
import UiCardContent from '@/components/ui/card-content.vue';
import UiButton from '@/components/ui/button.vue';
import UiInput from '@/components/ui/input.vue';
import UiLabel from '@/components/ui/label.vue';

export default {
  name: 'LoginView',
  components: {
    UiCard,
    UiCardContent,
    UiButton,
    UiInput,
    UiLabel
  },
  data() {
    return {
      credentials: {
        Email: '',
        Password: ''
      },
      loading: false,
      error: null,
      sessionExpired: false,
      registrationSuccess: false,
      redirectPath: '/'
    };
  },
  created() {
    const route = useRoute();
    
    // Check if redirected due to expired token
    if (route.query.expired === 'true') {
      this.sessionExpired = true;
    }
    
    // Check if redirected after successful registration
    if (route.query.registered === 'true') {
      this.registrationSuccess = true;
    }
    
    // Save redirect path if provided
    if (route.query.redirect) {
      this.redirectPath = route.query.redirect;
    }
  },
  methods: {
    ...mapActions(['login']),
    async handleLogin() {
      this.loading = true;
      this.error = null;
      this.sessionExpired = false;
      this.registrationSuccess = false;
      
      try {
        await this.login(this.credentials);
        this.$router.push(this.redirectPath);
      } catch (error) {
        console.error('Login error:', error);
        this.error = error.response?.data?.message || 'Login failed. Please check your credentials.';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
